import { FC, useMemo, useState } from "react";
import type { FileRejection } from "react-dropzone";
import { FormikProps } from "formik";
import currencyCodes from "currency-codes";
import countries from "world-countries";

import { DocumentIcon } from "../../../assets/icons";
import { Button, CardList, Combobox, Dropzone } from "../../../components";
import { formatBytes } from "../../../utils";
import {
  CardVariantType,
  DisplayVariant,
} from "../../../components/CardList/CardList";
import { DrawdownFields } from "../../LoanCalculator/service";
import { InvoicesList } from "./InvoicesList";
import { useGetApplicationVendors } from "../../../hooks";
import {
  LoanProductType,
  LoanVendorStatus,
  LoanVendorType,
  MediaPurpose,
} from "../../../hooks/api/types";
import { useLoanDashboard } from "../../../contexts";
import { getFileIcon } from "../../../utils/fileIcon.util";
import { getDropzoneAcceptForPurpose } from "../../../utils/accept.util";

type DrawdownFormProps = {
  formik: FormikProps<DrawdownFields>;
  availableLimit: number;
  maxDiscountLimit: number;
  loanApplicationId: string;
  previousStep?: () => void;
  loading?: boolean;
  error?: string;
};

export const DrawdownForm: FC<DrawdownFormProps> = ({
  formik,
  availableLimit,
  maxDiscountLimit,
  loanApplicationId,
  previousStep,
  loading,
  error,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>("");
  const {
    state: { currentLoanApplication },
  } = useLoanDashboard();

  const productType =
    currentLoanApplication?.loanProduct?.type ||
    currentLoanApplication?.loanType;
  const isPayablesFacility =
    productType === LoanProductType.LOC_PAYABLE_FINANCING;

  const { data: vendors } = useGetApplicationVendors({
    loanApplicationId,
    pageSize: Number.MAX_SAFE_INTEGER,
    status: LoanVendorStatus.APPROVED_BY_UW,
    type: isPayablesFacility ? LoanVendorType.SUPPLIER : LoanVendorType.BUYER,
  });
  const vendorOptions =
    vendors?.data?.map((vendor) => {
      const countryFlag = countries.find(
        (country) => country.cca3 === vendor.country
      )?.flag;
      return {
        value: vendor.vendorId,
        label: vendor.name,
        flag: countryFlag,
      };
    }) ?? [];

  const vendorLabel = isPayablesFacility ? "Supplier name" : "Buyer name";

  // Older implementation: returns all currencies
  // const currencyOptions: { value: string; label: string }[] = currencyCodes
  //   .codes()
  //   .map((code) => {
  //     const currency = currencyCodes.code(code);
  //     return currency ? { value: code, label: code } : undefined;
  //   })
  //   .filter((item): item is { value: string; label: string } => Boolean(item));

  const currencyOptions: { value: string; label: string }[] = [
    { value: "AED", label: "AED" },
    { value: "USD", label: "USD" },
    { value: "GBP", label: "GBP" },
    { value: "EUR", label: "EUR" },
  ];

  const handleFileClose = (index: number) => {
    const nextFiles = values.invoiceFiles.filter((_, i) => i !== index);
    setFieldValue("invoiceFiles", nextFiles);
    if (nextFiles.length <= 10) {
      setUploadError("");
    }
  };

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    setFieldTouched,
    setFieldValue,
    isValid,
    dirty,
  } = formik;

  const selectedVendorOption = useMemo(() => {
    if (!values.vendorId) {
      return undefined;
    }
    return vendorOptions.find((option) => option.value === values.vendorId);
  }, [values.vendorId, vendorOptions]);

  const handleUpload = (files: File[]) => {
    if (!files?.length) return;
    setUploading(true);
    const existingFiles = values.invoiceFiles || [];
    const availableSlots = Math.max(0, 10 - existingFiles.length);
    const MAX_SIZE = 5 * 1024 * 1024;

    const oversized = files.filter((f) => f.size > MAX_SIZE);
    const sizeOk = files.filter((f) => f.size <= MAX_SIZE);

    const filesToAdd = sizeOk.slice(0, availableSlots);

    if (!filesToAdd.length) {
      if (oversized.length) {
        setUploadError("Each file must be 5 MB or less.");
      } else {
        setUploadError("You can upload up to 10 files.");
      }
      setUploading(false);
      return;
    }

    if (sizeOk.length > availableSlots) {
      setUploadError("You can upload up to 10 files.");
    } else if (oversized.length) {
      setUploadError("Each file must be 5 MB or less.");
    } else {
      setUploadError("");
    }

    setFieldValue("invoiceFiles", [...existingFiles, ...filesToAdd]);
    setUploading(false);
  };

  const handleDropRejected = (rejections: FileRejection[]) => {
    if (!rejections?.length) return;
    const messages = new Set<string>();
    rejections.forEach((rej) => {
      rej.errors.forEach((err) => {
        switch (err.code) {
          case "file-too-large":
            messages.add("Each file must be 5 MB or less.");
            break;
          case "too-many-files":
            messages.add("You can upload up to 10 files.");
            break;
          case "file-invalid-type":
            messages.add("Only PDF files are allowed.");
            break;
          default:
            if (err.message) messages.add(err.message);
        }
      });
    });
    setUploadError(Array.from(messages).join(" "));
    setUploading(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="cx-bg-white cx-rounded-2xl cx-p-6 cx-mb-6 cx-shadow-base">
        <div className="cx-flex cx-flex-col cx-gap-1 cx-mb-6">
          <h2 className="cx-text-lg cx-font-semibold">Basic details</h2>
        </div>
        <div className="cx-grid cx-grid-cols-1 md:cx-grid-cols-2 cx-gap-4">
          <div className="cx-mb-2">
            <Combobox
              placeholder={vendorLabel}
              options={vendorOptions}
              className="cx-rounded-lg"
              showSelectedIndicator
              inputProps={{
                name: "vendorId",
                value: selectedVendorOption,
                onBlur: () => setFieldTouched("vendorId"),
              }}
              onChange={(selectedValue) => {
                setFieldValue("vendorId", selectedValue || "");
                setFieldTouched("vendorId", true, false);
              }}
              error={touched.vendorId ? errors.vendorId : ""}
            />
          </div>
          <div className="cx-mb-2">
            <Combobox
              placeholder="Currency"
              options={currencyOptions}
              className="cx-rounded-lg"
              defaultValue={currencyOptions.find(
                (option) => option.value === "AED"
              )}
              showSelectedIndicator
              inputProps={{
                name: "currency",
                onBlur: () => setFieldTouched("currency"),
              }}
              onChange={handleChange("currency")}
              error={touched.currency ? errors.currency : ""}
            />
          </div>
        </div>
      </div>

      <InvoicesList
        availableLimit={availableLimit}
        maxDiscountLimit={maxDiscountLimit}
        formik={formik}
      />
      <div className="cx-w-full">
        <div className="cx-bg-white cx-rounded-2xl cx-p-6 cx-mb-2 cx-shadow-base">
          <div className="cx-flex cx-flex-col cx-gap-1 cx-mb-4">
            <h2 className="cx-text-lg cx-font-semibold">
              Upload invoice & supporting documents
            </h2>
            <p className="cx-text-sm cx-text-gray-500">
              Please upload your invoices and credit notes individually or
              combine them into a single PDF.
            </p>
          </div>

          <Dropzone
            variant={uploading ? "uploading" : "invoice"}
            extensions={["Supports up to 10 files (max 5 MB each)"]}
            showExtensions={false}
            options={{
              accept: getDropzoneAcceptForPurpose(
                MediaPurpose.DRAWDOWN_INVOICE
              ),
              maxFiles: 10,
              maxSize: 5 * 1024 * 1024,
              onDropRejected: handleDropRejected,
            }}
            onDrop={(files) => {
              handleUpload(files);
            }}
            error={uploadError}
          />

          <div className="cx-w-full cx-space-y-2 cx-mt-3 ">
            {values.invoiceFiles.map((invoice: File, index: number) => (
              <CardList
                key={index}
                variant={
                  uploading
                    ? CardVariantType.UPLOADING
                    : invoice.name
                    ? CardVariantType.UPLOADED
                    : CardVariantType.UPLOAD
                }
                title={invoice.name}
                description={formatBytes(invoice.size, 2)}
                inputProps={{ name: "invoice" }}
                displayVariant={DisplayVariant.NEW}
                icon={
                  uploading ? (
                    <DocumentIcon width="100%" height="100%" />
                  ) : (
                    getFileIcon(invoice)
                  )
                }
                handleFileClose={() => handleFileClose(index)}
                onChange={async (file) => {
                  if (!file) return;
                  handleUpload([file]);
                }}
                error={
                  touched.invoiceFiles?.[index] &&
                  !!errors.invoiceFiles?.[index]
                }
              />
            ))}
          </div>
          {touched.invoiceFiles &&
            errors.invoiceFiles &&
            typeof errors.invoiceFiles === "string" && (
              <div className="cx-text-text-error cx-text-xs ">
                {errors.invoiceFiles}
              </div>
            )}
        </div>

        <div className="cx-text-red-500 cx-text-sm cx-p-2 cx-mb-2">{error}</div>

        <div className="cx-border-t cx-border-[#D9D9D9] cx-mb-8 cx-pt-8 cx-flex cx-justify-end cx-gap-4 cx-flex-wrap">
          <Button
            label="Cancel"
            outlined
            onClick={previousStep}
            disabled={uploading || loading}
            slim
            className="cx-px-16 cx-w-44"
          />
          <Button
            label="Submit"
            disabled={
              uploading ||
              loading ||
              !values.invoices.length ||
              !values.invoiceFiles.length ||
              !isValid ||
              !dirty
            }
            loader={loading ? "right" : undefined}
            type="submit"
            slim
            className="cx-px-16 cx-w-44"
          />
        </div>
      </div>
    </form>
  );
};

export default DrawdownForm;
